/**
 * Sharing UX Module
 * Floating pill UI for sharing calc scenarios via URL params.
 *
 * Only activates on single calc pages (body.single-layout).
 * Tracks changes to assignment rows and encodes them as URL params.
 */

const STYLES = `
/* Sharing UX Styles */
:root {
  --sharing-accent: #60a5fa;
  --sharing-green: #34d399;
  --sharing-bg-card: #1a1f2e;
  --sharing-border: #2a3441;
  --sharing-text: #e8eaed;
  --sharing-text-dim: #8b949e;
  --sharing-text-muted: #5a6270;
  --sharing-bg-input: #252d3a;
  --sharing-bg: #0f1419;
}

/* ===== SCENARIO PILL ===== */
.scenario-pill {
  position: fixed;
  bottom: 24px;
  right: 24px;
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--sharing-bg-card);
  border: 1px solid var(--sharing-border);
  border-radius: 24px;
  padding: 10px 16px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
  opacity: 0;
  transform: translateY(20px);
  pointer-events: none;
  box-shadow: 0 4px 20px rgba(0,0,0,0.3);
  z-index: 9999;
  font-family: 'Fira Sans', -apple-system, sans-serif;
  color: var(--sharing-text);
}

.scenario-pill.visible {
  opacity: 1;
  transform: translateY(0);
  pointer-events: auto;
}

.scenario-pill:hover {
  border-color: var(--sharing-accent);
  box-shadow: 0 4px 24px rgba(96, 165, 250, 0.2);
}

.scenario-pill.expanded {
  border-radius: 12px;
  flex-direction: column;
  align-items: stretch;
  padding: 16px;
  width: 320px;
  cursor: default;
}

.pill-dot {
  width: 8px;
  height: 8px;
  background: var(--sharing-accent);
  border-radius: 50%;
  animation: sharing-pulse 2s infinite;
}

@keyframes sharing-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.pill-text {
  color: var(--sharing-text-dim);
}

.pill-text strong {
  color: var(--sharing-text);
}

.pill-arrow {
  color: var(--sharing-text-muted);
  transition: transform 0.2s;
  display: flex;
}

.scenario-pill.expanded .pill-arrow {
  transform: rotate(180deg);
}

/* Expanded state */
.pill-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.pill-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
}

.pill-close {
  background: none;
  border: none;
  color: var(--sharing-text-muted);
  cursor: pointer;
  padding: 4px;
  display: flex;
}

.pill-close:hover {
  color: var(--sharing-text);
}

.pill-body {
  display: none;
}

.scenario-pill.expanded .pill-body {
  display: block;
}

.scenario-pill.expanded .pill-collapsed {
  display: none;
}

.pill-collapsed {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pill-changes {
  background: var(--sharing-bg);
  border-radius: 6px;
  padding: 10px 12px;
  margin-bottom: 12px;
  font-size: 12px;
  max-height: 120px;
  overflow-y: auto;
}

.change-item {
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
  color: var(--sharing-text-dim);
}

.change-item .new-val {
  font-family: 'JetBrains Mono', monospace;
  color: var(--sharing-accent);
}

.change-item .old-val {
  color: var(--sharing-text-muted);
  font-size: 11px;
}

.pill-url {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.url-box {
  flex: 1;
  background: var(--sharing-bg);
  border: 1px solid var(--sharing-border);
  border-radius: 6px;
  padding: 8px 10px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  color: var(--sharing-text-dim);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.url-box .params {
  color: var(--sharing-accent);
}

.copy-btn {
  background: var(--sharing-accent);
  border: none;
  border-radius: 6px;
  padding: 8px 14px;
  color: white;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background 0.15s;
}

.copy-btn:hover {
  background: #4a94f0;
}

.copy-btn.copied {
  background: var(--sharing-green);
}

.pill-actions {
  display: flex;
  gap: 8px;
}

.pill-action {
  flex: 1;
  background: var(--sharing-bg-input);
  border: 1px solid var(--sharing-border);
  border-radius: 6px;
  padding: 8px;
  font-size: 11px;
  color: var(--sharing-text-dim);
  cursor: pointer;
  text-align: center;
  transition: all 0.15s;
}

.pill-action:hover {
  border-color: var(--sharing-text-muted);
  color: var(--sharing-text);
}

.pill-action.active {
  border-color: var(--sharing-accent);
  background: rgba(96, 165, 250, 0.1);
  color: var(--sharing-text);
}
`;

const TEMPLATE = `
  <div class="scenario-pill" id="scenarioPill">
    <div class="pill-collapsed">
      <span class="pill-dot"></span>
      <span class="pill-text"><strong id="changeCount">0</strong> changes</span>
      <span class="pill-arrow">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="18 15 12 9 6 15"></polyline>
        </svg>
      </span>
    </div>
    <div class="pill-body">
      <div class="pill-header">
        <div class="pill-title">
          <span class="pill-dot"></span>
          Your Scenario
        </div>
        <button class="pill-close" id="pillClose">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
      <div class="pill-changes" id="pillChanges"></div>
      <div class="pill-url">
        <div class="url-box">
          <span class="base" id="urlBase"></span><span class="params" id="urlParams"></span>
        </div>
        <button class="copy-btn" id="copyBtn">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
          </svg>
          Copy
        </button>
      </div>
      <div class="pill-actions">
        <button class="pill-action active" data-mode="scenario">Share scenario</button>
        <button class="pill-action" data-mode="original">Share original</button>
        <button class="pill-action" id="resetBtn">Reset</button>
      </div>
    </div>
  </div>
`;

class SharingUX {
  constructor() {
    this.originals = {};      // { rowIndex: originalText }
    this.shareMode = 'scenario';
    this.expanded = false;
    this.initialized = false;
    this.sharedCalcId = null; // For shared calc pages like /59001
    this.resetting = false;   // Flag to ignore events during reset
    this.applyingOverrides = false;
    this.updateTimer = null;

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.checkAndInit());
    } else {
      setTimeout(() => this.checkAndInit(), 500);
    }
  }

  checkAndInit() {
    if (!document.body.classList.contains('single-layout')) return;

    // Extract calc ID from URL path (e.g., /59001 -> "59001")
    const match = window.location.pathname.match(/^\/(\d+)\/?$/);
    this.sharedCalcId = match ? match[1] : null;

    this.init();
  }

  init() {
    if (this.initialized) return;
    this.injectStyles();
    this.injectHTML();
    this.attachListeners();
    this.waitForCalc();
    this.initialized = true;
  }

  injectStyles() {
    const style = document.createElement('style');
    style.id = 'sharing-ux-styles';
    style.textContent = STYLES;
    document.head.appendChild(style);
  }

  injectHTML() {
    const div = document.createElement('div');
    div.id = 'sharing-ux-container';
    div.innerHTML = TEMPLATE;
    document.body.appendChild(div);
  }

  attachListeners() {
    this.pill = document.getElementById('scenarioPill');

    // Set base URL
    const url = new URL(window.location);
    url.search = '';
    document.getElementById('urlBase').textContent = url.host + url.pathname;

    // Toggle expansion
    this.pill.addEventListener('click', (e) => {
      if (e.target.closest('.pill-body')) return;
      if (!this.expanded) {
        this.pill.classList.add('expanded');
        this.expanded = true;
      }
    });

    document.getElementById('pillClose').addEventListener('click', () => {
      this.pill.classList.remove('expanded');
      this.expanded = false;
    });

    // Share mode toggle
    document.querySelectorAll('.pill-action[data-mode]').forEach(btn => {
      btn.addEventListener('click', () => {
        document.querySelectorAll('.pill-action[data-mode]').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        this.shareMode = btn.dataset.mode;
        this.updateURL();
      });
    });

    document.getElementById('resetBtn').addEventListener('click', () => this.reset());
    document.getElementById('copyBtn').addEventListener('click', () => this.copy());

    if (window.InstacalcEvents) {
      this.unsubscribeCalcChanged = window.InstacalcEvents.on(
        window.InstacalcEvents.CALC_CHANGED,
        () => this.queueUpdate()
      );
    }
  }

  /**
   * Wait for calc data, then capture originals.
   * For shared calc pages (/59001), use Embed.get() as source of truth.
   * For regular pages, use Action.getCurrentCalc().
   */
  waitForCalc(retries = 0) {
    const MAX_RETRIES = 20;

    // Helper to count content rows
    const countContentRows = (rows) => rows.filter(r => {
      const text = r.text?.trim() || '';
      return text.startsWith('//') || /^\s*[a-zA-Z_][\w\s]*?\s*=/.test(text);
    }).length;

    // For shared calc pages, get originals from Embed store
    if (this.sharedCalcId && window.Embed?.get) {
      const embedCalc = window.Embed.get(this.sharedCalcId);
      const actionCalc = window.Action?.getCurrentCalc?.();
      // Wait for at least 5 content rows to ensure data is loaded
      if (embedCalc?.rows?.length > 0 && countContentRows(embedCalc.rows) >= 5 && actionCalc?.rows?.length) {
        this.captureOriginals(embedCalc);
        this.applyOverridesFromUrl(actionCalc);
        this.listenForChanges();
        return;
      }
      // Embed not ready yet, retry
      if (retries < MAX_RETRIES) {
        setTimeout(() => this.waitForCalc(retries + 1), 300);
      }
      return;
    }

    // Fallback: use Action.getCurrentCalc()
    const calc = window.Action?.getCurrentCalc?.();
    if (!calc?.rows?.length) {
      if (retries < MAX_RETRIES) {
        setTimeout(() => this.waitForCalc(retries + 1), 300);
      }
      return;
    }

    // For non-shared pages, wait for content before capturing
    if (countContentRows(calc.rows) < 5 && retries < MAX_RETRIES) {
      setTimeout(() => this.waitForCalc(retries + 1), 300);
      return;
    }

    this.captureOriginals(calc);
    this.applyOverridesFromUrl(calc);
    this.listenForChanges();
  }

  captureOriginals(calc) {
    calc.rows.forEach((row, i) => {
      this.originals[i] = row.text || '';
    });
  }

  listenForChanges() {
    // Dead code removed - CALC_MODIFIED event never existed
    // This method is kept for API compatibility but does nothing
  }

  /**
   * Get changes between originals and current values
   */
  getChanges() {
    const calc = window.Action?.getCurrentCalc?.();
    if (!calc?.rows) return [];

    const assignmentRows = this.getAssignmentRows(calc.rows);
    const assignmentByIndex = new Map(assignmentRows.map(row => [row.index, row]));

    const changes = [];
    calc.rows.forEach((row, index) => {
      const original = this.originals[index];
      const current = row.text || '';

      if (original === undefined || current === original) return;

      const assignment = assignmentByIndex.get(index);
      if (!assignment) return;

      const label = assignment.paramName || `Row ${index + 1}`;
      changes.push({
        index,
        paramKey: assignment.paramKey,
        label,
        oldVal: this.extractAssignmentValue(original),
        newVal: this.extractAssignmentValue(current)
      });
    });

    return changes;
  }

  updatePill() {
    const changes = this.getChanges();
    const countEl = document.getElementById('changeCount');
    const listEl = document.getElementById('pillChanges');

    if (changes.length > 0) {
      this.pill.classList.add('visible');
      countEl.textContent = changes.length;
      listEl.innerHTML = changes.map(c => `
        <div class="change-item">
          <span>${this.escapeHtml(c.label)}</span>
          <span>
            <span class="new-val">${this.escapeHtml(c.newVal)}</span>
            <span class="old-val">(was ${this.escapeHtml(c.oldVal)})</span>
          </span>
        </div>
      `).join('');
    } else {
      this.pill.classList.remove('visible', 'expanded');
      this.expanded = false;
    }
  }

  updateURL() {
    if (this.applyingOverrides) return;
    const changes = this.getChanges();
    const url = new URL(window.location);
    const calc = window.Action?.getCurrentCalc?.();

    if (calc?.rows?.length) {
      const overrideKeys = this.getOverrideParamKeySet(calc.rows);
      [...url.searchParams.keys()].forEach(key => {
        if (overrideKeys.has(key.toLowerCase())) {
          url.searchParams.delete(key);
        }
      });
    } else {
      [...url.searchParams.keys()]
        .filter(k => /^r\d+$/.test(k))
        .forEach(k => url.searchParams.delete(k));
    }

    // Add new params if sharing scenario
    if (this.shareMode === 'scenario') {
      changes.forEach(c => url.searchParams.set(c.paramKey, c.newVal));
    }

    window.history.replaceState({}, '', url);
    document.getElementById('urlParams').textContent =
      changes.length && this.shareMode === 'scenario' ? '?' + url.searchParams.toString() : '';
  }

  reset() {
    const calc = window.Action?.getCurrentCalc?.();
    if (!calc?.rows) return;

    this.resetting = true;

    Object.entries(this.originals).forEach(([index, original]) => {
      const i = parseInt(index, 10);
      if (i >= 0 && i < calc.rows.length && calc.rows[i].text !== original) {
        window.Action?.updateRowText?.(calc, i, original, { immediate: true });
      }
    });

    // Delay to let row updates propagate, then update UI
    setTimeout(() => {
      this.resetting = false;
      this.updatePill();
      this.updateURL();
    }, 100);
  }

  copy() {
    navigator.clipboard.writeText(window.location.href);
    const btn = document.getElementById('copyBtn');
    btn.classList.add('copied');
    const original = btn.innerHTML;
    btn.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"></polyline></svg> Copied!`;
    setTimeout(() => {
      btn.classList.remove('copied');
      btn.innerHTML = original;
    }, 2000);
  }

  escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  queueUpdate() {
    if (this.resetting || this.applyingOverrides) return;
    clearTimeout(this.updateTimer);
    this.updateTimer = setTimeout(() => {
      if (this.resetting || this.applyingOverrides) return;
      this.updatePill();
      this.updateURL();
    }, 250);
  }

  applyOverridesFromUrl(calc) {
    const params = new URLSearchParams(window.location.search || '');
    const assignmentRows = this.getAssignmentRows(calc.rows);
    if (!assignmentRows.length) return;

    const byName = new Map();
    assignmentRows.forEach(row => {
      if (row.paramName) {
        byName.set(row.paramName.toLowerCase(), row);
      }
    });

    const updates = [];
    params.forEach((value, key) => {
      const lowerKey = key.toLowerCase();
      if (/^r\d+$/.test(lowerKey)) {
        const position = parseInt(lowerKey.slice(1), 10);
        const target = assignmentRows[position - 1];
        if (target) {
          updates.push({ index: target.index, value });
        }
        return;
      }
      const target = byName.get(lowerKey);
      if (target) {
        updates.push({ index: target.index, value });
      }
    });

    if (!updates.length) return;

    this.applyingOverrides = true;

    updates.forEach(({ index, value }) => {
      const row = calc.rows[index];
      if (!row) return;
      const currentValue = this.extractAssignmentValue(row.text || '');
      if (currentValue === String(value)) return;
      const updated = this.buildUpdatedAssignmentText(row.text || '', value);
      window.Action?.updateRowText?.(calc, index, updated, { immediate: true });
    });

    setTimeout(() => {
      this.applyingOverrides = false;
      this.updatePill();
      this.updateURL();
    }, 100);
  }

  getAssignmentRows(rows) {
    const result = [];
    let position = 0;
    const usedNames = new Set();
    rows.forEach((row, index) => {
      const text = row?.text || '';
      const paramName = this.getParameterName(text);
      if (!paramName) return;
      position += 1;
      let paramKey = paramName;
      const lowerName = paramName.toLowerCase();
      if (usedNames.has(lowerName)) {
        paramKey = `r${position}`;
      } else {
        usedNames.add(lowerName);
      }
      result.push({
        index,
        position,
        paramName,
        paramKey
      });
    });
    return result;
  }

  getOverrideParamKeySet(rows) {
    const keys = new Set();
    const assignmentRows = this.getAssignmentRows(rows);
    assignmentRows.forEach(row => {
      keys.add(`r${row.position}`.toLowerCase());
      if (row.paramName) {
        keys.add(row.paramName.toLowerCase());
      }
    });
    return keys;
  }

  getParameterName(text) {
    const apiName = window.IC?.api?.getParameterNameForDisplay?.(text);
    if (apiName && apiName.trim()) return apiName.trim();
    return null;
  }

  extractAssignmentValue(text) {
    const rawText = text || '';
    const eq = rawText.indexOf('=');
    const colon = rawText.indexOf(':');
    const idx = eq >= 0 ? eq : colon;
    if (idx < 0) return rawText.trim();
    const afterEq = rawText.substring(idx + 1);
    const comment = afterEq.indexOf('//');
    return (comment >= 0 ? afterEq.substring(0, comment) : afterEq).trim();
  }

  buildUpdatedAssignmentText(text, value) {
    const rawText = text || '';
    const eq = rawText.indexOf('=');
    const colon = rawText.indexOf(':');
    const idx = eq >= 0 ? eq : colon;
    if (idx < 0) return text;
    const separator = eq >= 0 ? '=' : ':';
    const left = rawText.slice(0, idx).trimEnd();
    const after = rawText.slice(idx + 1);
    const commentIndex = after.indexOf('//');
    const comment = commentIndex >= 0 ? after.slice(commentIndex) : '';
    return `${left} ${separator} ${value}${comment ? ' ' + comment.replace(/^\s+/, '') : ''}`;
  }
}

window.SharingUX = new SharingUX();
